import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { translations, Language, DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES, loadLanguage } from "@/i18n/translations";

const isMissingTranslationValue = (value: string, key: string) => value === key;

const fallbackTranslations: Partial<Record<string, string>> = {
  "institutional.complianceBlocked": "Acceso bloqueado por incumplimiento semanal",
  "institutional.completeExamToUnlock": "Completa el examen semanal para desbloquear la práctica por módulo.",
  "institutional.baselineRequired": "Debes completar tu examen Baseline primero",
  "institutional.completeBaselineToUnlock": "Ve a tu dashboard para iniciar el examen Baseline.",
};

// Re-export Language type for backwards compatibility
export type { Language } from "@/i18n/translations";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, vars?: Record<string, string | number>) => string;
  detectBrowserLanguage: () => Language;
  isTranslating: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Detect browser language - only return languages we have translations for
  const detectBrowserLanguage = (): Language => {
    const browserLang = navigator.language.toLowerCase().split('-')[0];
    return AVAILABLE_LANGUAGES.includes(browserLang as Language) 
      ? browserLang as Language 
      : DEFAULT_LANGUAGE;
  };

  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    if (saved && AVAILABLE_LANGUAGES.includes(saved as Language)) {
      return saved as Language;
    }
    return DEFAULT_LANGUAGE;
  });
  const [, forceRerender] = useState(0);

  // Ensure the non-default language dictionary is loaded whenever the active
  // language is not English. Triggers a rerender once the chunk is in.
  useEffect(() => {
    if (language === DEFAULT_LANGUAGE) return;
    let cancelled = false;
    loadLanguage(language).then(() => {
      if (!cancelled) forceRerender((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, [language]);

  // Sync html lang attribute whenever language changes
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Load user's preferred language from profile on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_language')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (profile?.preferred_language) {
            const userLang = profile.preferred_language as Language;
            if (AVAILABLE_LANGUAGES.includes(userLang)) {
              setLanguageState(userLang);
              localStorage.setItem('language', userLang);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user language:', error);
      }
    };

    loadUserLanguage();
  }, []);

  const setLanguage = async (lang: Language) => {
    await loadLanguage(lang);
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Save to user profile in database
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await supabase
          .from('profiles')
          .update({ preferred_language: lang })
          .eq('user_id', user.id);
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  const t = (key: string, vars?: Record<string, string | number>): string => {
    const currentTranslations = translations[language];
    const resolved =
      currentTranslations[key as keyof typeof currentTranslations] ||
      translations[DEFAULT_LANGUAGE][key as keyof typeof translations["en"]] ||
      key;
    const template = isMissingTranslationValue(resolved, key)
      ? fallbackTranslations[key] || key
      : resolved;

    if (!vars) return template;

    return template.replace(/\{(\w+)\}/g, (_m, varName: string) => {
      const value = vars[varName];
      return value === undefined || value === null ? `{${varName}}` : String(value);
    });
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, detectBrowserLanguage, isTranslating: false }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // During hot reload in development, context may not be available
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.warn("useLanguage: Context not ready, using fallback");
      const fallbackT = (key: string, vars?: Record<string, string | number>) => {
        const resolved =
          translations[DEFAULT_LANGUAGE][key as keyof typeof translations["en"]] ||
          fallbackTranslations[key] ||
          key;

        if (!vars) return resolved;

        return resolved.replace(/\{(\w+)\}/g, (_m, varName: string) => {
          const value = vars[varName];
          return value === undefined || value === null ? `{${varName}}` : String(value);
        });
      };

      return {
        language: DEFAULT_LANGUAGE as Language,
        setLanguage: async () => {},
        t: fallbackT,
        detectBrowserLanguage: () => DEFAULT_LANGUAGE as Language,
        isTranslating: false,
      };
    }
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
